import { PrintServiceProductEntityHydrated } from "@jackfruit/common"

export const createImageFromUrl = async (url: string) => {
  const response = await fetch(
    "https://files.printicular.com/production/uploads/product-template-thumbnails/6200d3d033718211b6c3095e216d8e66"
  )
  const blob = await response.blob()
  const file = new File([blob], "image.png", { type: blob.type })

  return file
}

const createImageFromCanvas = async (
  width = 400,
  height = 400
): Promise<File> => {
  const canvas = document.createElement("canvas")
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext("2d")
  if (!ctx) {
    throw new Error("Failed to get canvas context")
  }

  // Fill background with white
  ctx.fillStyle = "white"
  ctx.fillRect(0, 0, width, height)

  const lines = ["CUSTOMIZABLE", "YOUR", "IMAGE", "GOES HERE"]
  const fontWeights = ["normal", "bold", "bold", "normal"]

  const baseSpacing = 12
  const lineSpacings = [
    baseSpacing * 3, // after line 0
    baseSpacing / 2, // after line 1
    baseSpacing * 3, // after line 2
    0, // last line, no spacing needed after
  ]

  const maxWidth = width * 0.6
  const maxHeight = height * 0.6

  ctx.textAlign = "center"
  ctx.textBaseline = "middle"
  ctx.fillStyle = "#212121"

  // Determine a shared target text width
  let tempFontSize = 10
  let targetTextWidth = 0

  lines.forEach((line, i) => {
    tempFontSize = 10
    while (true) {
      ctx.font = `${fontWeights[i]} ${tempFontSize + 1}px sans-serif`
      const textWidth = ctx.measureText(line).width
      const estimatedHeight =
        (tempFontSize + 1) * lines.length +
        lineSpacings.reduce((sum, spacing) => sum + spacing, 0)
      if (textWidth > maxWidth || estimatedHeight > maxHeight) break
      tempFontSize++
    }
    ctx.font = `${fontWeights[i]} ${tempFontSize}px sans-serif`
    const finalWidth = ctx.measureText(line).width
    if (finalWidth > targetTextWidth) {
      targetTextWidth = finalWidth
    }
  })

  // Compute font size for each line to match targetTextWidth
  const lineFontSizes = lines.map((line, i) => {
    let size = 5
    while (true) {
      ctx.font = `${fontWeights[i]} ${size + 1}px sans-serif`
      const w = ctx.measureText(line).width
      if (w >= targetTextWidth) break
      size++
    }
    return size
  })

  // Compute vertical positioning
  const totalTextHeight = lineFontSizes.reduce((sum, size, i) => {
    return sum + size + (i < lines.length - 1 ? lineSpacings[i] : 0)
  }, 0)

  const startY = (height - totalTextHeight) / 2

  // Draw each line
  let currentY = startY
  lines.forEach((line, i) => {
    const fontSize = lineFontSizes[i]
    ctx.font = `${fontWeights[i]} ${fontSize}px sans-serif`
    ctx.fillText(line, width / 2, currentY + fontSize / 2)
    currentY += fontSize + (i < lines.length - 1 ? lineSpacings[i] : 0)
  })

  return new Promise(resolve => {
    canvas.toBlob(blob => {
      const file = new File([blob!], "image.png", { type: "image/png" })
      resolve(file)
    }, "image/png")
  })
}

export const createImageFromProduct = async (
  product: PrintServiceProductEntityHydrated
) => {
  const { pixelWidth, pixelHeight } = product

  return createImageFromCanvas(pixelWidth, pixelHeight)
}
